import * as React from "react";
import cx from "classnames";
import { I18n } from "react-redux-i18n";

import { NewTable, NewTableSelection } from "@common/components/new-table";
import { MODULE_NAME } from "@modules/pedigrees/constants/shared";

import { useController } from './invites-table.controller';
import { InvitesTableProps } from './invites-table.types';
import { NEW_INVITE_STEPS, NewInvite } from '@common/components/new-invite';
import { QuestionnaireOpen } from '@common/components/questionnaire-open';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';
import { SaveIndividualEmailParams, SendInviteParams } from '@common/actions/individuals';
import { getReviewedStatusState } from '@common/actions/invites';
import { getEmailTemplates } from '@common/actions/email-templates';
import { ViewInviteHistory } from "@common/components/view-invite-history";
import { UpdateStatusConfig } from "@common/components/update-status-config";
import { InviteListDto } from "@common/dto/invite/list-dto";

import './invites-table.styles.scss'

export const InvitesTable: React.FC<InvitesTableProps> = (props) => {
  const { 
    filters,
    itemUniqProperty,
    selectedItems,
    data,
    totalItemsCount,
    loadMetadata,
    className,
    loading,
    getQuestionnaireForInvite,
    getFoldersForAddingColumns,
    onSelectedItemsChange,
    getFamilyIdMembersCount,
    getIndividualFamilyData,
    getPedigreeFolders,
    createPedigreeAndMoveToFolder,
    checkPedigreeNameExists,
    getQuestionnaires,
    sendInvites,
    showErrorMessage,
    showInfoMessage,
    getIndividualEmail,
    getSentQuestionnaires,
    saveIndividualEmail,
  } = props;
  const {
    batchSendReminderProps,
    closeBatchSendReminder,
    handleBatchSendReminderSend,
    closeBatchNewInvites,
    handleBatchNewInvitesSend,
    batchNewInvitesProps,
    getQuestionnairesForBatchInvites,
    handleSendReminderSend,
    sendReminderProps,
    closeSendReminder,
    newInviteProps,
    closeNewInvite,
    handleNewInviteSend,
    closeOpenQuestionnaire,
    handleOpenQuestionnaire,
    openQuestionnaireProps,
    changingColumns,
    handleColumnAdd,
    handleColumnRemove,
    handleGetFields,
    getHeaderElementId,
    getHeaderCellElementId,
    getBodyRowElementId,
    getBodyCellElementId,
    columnActions,
    headerActions,
    tableColumns,
    getData,
    handleColumnsResizing,
    handleColumnsOrderChange,
    closeViewInviteHistoryModal,
    viewInviteHistoryProps,
    updateStatusProps,
    closeUpdateReviewedStatusModal,
    handleUpdateReviewedStatusSubmit
  } = useController(props);

  return (
    <>
      {viewInviteHistoryProps && (
        <ViewInviteHistory
          onClose={closeViewInviteHistoryModal}
          individual={viewInviteHistoryProps.individual}
          invite={viewInviteHistoryProps.invite}
          showErrorMessage={props.showErrorMessage}
          getInviteHistory={props.getInviteHistory}
        />
      )}
      {updateStatusProps && (
        <UpdateStatusConfig 
          id={updateStatusProps.id}
          fhq4={updateStatusProps.fhq4!}
          getState={getReviewedStatusState}
          getFolders={getPedigreeFolders}
          getEmailTemplates={getEmailTemplates}
          showErrorMessage={showErrorMessage}
          close={closeUpdateReviewedStatusModal}
          onSubmit={handleUpdateReviewedStatusSubmit}
        />
      )}
      {newInviteProps && (
        <NewInvite
          getFamilyIdMembersCount={getFamilyIdMembersCount}
          getIndividualFamilyData={getIndividualFamilyData}
          getPedigreeFolders={getPedigreeFolders}
          createPedigreeAndMoveToFolder={createPedigreeAndMoveToFolder}
          checkPedigreeNameExists={checkPedigreeNameExists}
          getQuestionnaires={getQuestionnaires}
          sendInvite={(x: SendInviteParams) => sendInvites([x])}
          individualId={newInviteProps.individualId}
          showErrorMessage={showErrorMessage}
          showInfoMessage={showInfoMessage}
          getIndividualEmail={(questionnaire: QuestionnaireListDto) => {
            return getIndividualEmail({
              fhq4: questionnaire.fhq4,
              individualId: newInviteProps.individualId,
              questionnaireId: questionnaire.questionnaireId,
            });
          }}
          onClose={closeNewInvite}
          onSubmit={handleNewInviteSend}
          saveIndividualEmail={(params: SaveIndividualEmailParams) => saveIndividualEmail({ ...params, inviteId: newInviteProps.inviteId })}
        />
      )}
      {sendReminderProps && (
        <NewInvite
          getFamilyIdMembersCount={getFamilyIdMembersCount}
          getIndividualFamilyData={getIndividualFamilyData}
          getPedigreeFolders={getPedigreeFolders}
          createPedigreeAndMoveToFolder={createPedigreeAndMoveToFolder}
          checkPedigreeNameExists={checkPedigreeNameExists}
          getQuestionnaires={getQuestionnaires}
          getQuestionnaireForInvite={(inviteId: InviteListDto['id']) => getQuestionnaireForInvite(inviteId, sendReminderProps.fhq4)}
          individualId={sendReminderProps.individualId}
          inviteId={sendReminderProps.inviteId}
          getStepsConfig={() => ({
            [NEW_INVITE_STEPS.emailTemplate]: {
              submitBtnText: I18n.t('Send a Reminder')
            }
          })}
          getSteps={() => [
            {
              step: NEW_INVITE_STEPS.pedigreeFolder,
            },
            {
              step: NEW_INVITE_STEPS.emailAddress,
            },
            {
              step: NEW_INVITE_STEPS.emailTemplate,
            },
          ]}
          saveIndividualEmail={(params: SaveIndividualEmailParams) => saveIndividualEmail({ ...params, inviteId: sendReminderProps.inviteId })}
          showErrorMessage={showErrorMessage}
          getIndividualEmail={(questionnaire: QuestionnaireListDto) => {
            return getIndividualEmail({
              fhq4: questionnaire.fhq4,
              individualId: sendReminderProps.individualId,
              questionnaireId: questionnaire.questionnaireId,
            });
          }}
          onSubmit={handleSendReminderSend}
          onClose={closeSendReminder}
        />
      )}
      {batchNewInvitesProps.opened && (
        <NewInvite
          getPedigreeFolders={getPedigreeFolders}
          getQuestionnaires={getQuestionnairesForBatchInvites}
          showErrorMessage={showErrorMessage}
          getSteps={() => [
            {
              condition: true,
              step: NEW_INVITE_STEPS.questionnaires,
            },
            {
              condition: true,
              step: NEW_INVITE_STEPS.emailTemplate,
            }
          ]}
          onSubmit={handleBatchNewInvitesSend}
          onClose={closeBatchNewInvites}
        />
      )}
      {batchSendReminderProps && (
        <NewInvite
          getStepsConfig={() => ({
            [NEW_INVITE_STEPS.emailTemplate]: {
              submitBtnText: I18n.t('Send a Reminder')
            }
          })}
          defaultQuestionnaire={batchSendReminderProps.questionnaire}
          defaultEmailSubject={batchSendReminderProps.questionnaire.reminderEmailTemplate.subject}
          defaultEmailTemplate={batchSendReminderProps.questionnaire.reminderEmailTemplate.body}
          getSteps={() => [
            {
              condition: true,
              step: NEW_INVITE_STEPS.emailTemplate,
            }
          ]}
          getPedigreeFolders={getPedigreeFolders}
          getQuestionnaires={getQuestionnairesForBatchInvites}
          showErrorMessage={showErrorMessage}
          onSubmit={handleBatchSendReminderSend}
          onClose={closeBatchSendReminder}
        />
      )}
      {openQuestionnaireProps && (
        <QuestionnaireOpen
          itemName={I18n.t('patient')}
          showErrorMessage={showErrorMessage}
          onClose={closeOpenQuestionnaire}
          onSubmit={handleOpenQuestionnaire}
          getQuestionnaires={() => getSentQuestionnaires(openQuestionnaireProps.individualId)}
        />
      )}
    <NewTable 
      infiniteScroll
      disableAutoInit
      classes={{
        root: cx('PedigreesInvitesTable', className)
      }}
      columnsResizing={{
        onChange: handleColumnsResizing
      }}
      columnsOrder={{
        onChange: handleColumnsOrderChange
      }}
      headerActions={headerActions}
      columnActions={columnActions}
      getId={{
        header: getHeaderElementId,
        headerCell: getHeaderCellElementId,
        bodyRow: getBodyRowElementId,
        bodyCell: getBodyCellElementId,
      }}
      selection={{
        type: 'multiple',
        items: selectedItems,
        onChange: onSelectedItemsChange as NewTableSelection['onChange']
      }}
      columnsManagement={{
        onAdd: handleColumnAdd,
        onRemove: handleColumnRemove,
        getFolders: getFoldersForAddingColumns,
        getFields: handleGetFields,
      }}
      filters={filters}
      loading={{ list: !loadMetadata || loading, columns: changingColumns, }}
      moduleName={`${MODULE_NAME}_invites_table`}
      noDataMessage={I18n.t('There no invites to display')}
      errorMessage={{ list: I18n.t('Failed to load invites') }}
      itemUniqProperty={itemUniqProperty}
      data={data}
      columns={tableColumns}
      totalItemsCount={totalItemsCount}
      loadMetadata={loadMetadata}
      onLoadData={getData}
    />
    </>
  );
};
